import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { ArrowLeft, CalendarClock, DollarSign, Download, FileText, Wallet } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { SignInRequired } from "@/components/SignInRequired";
import { fetchPayoutStatement, downloadStatementCsv, downloadStatementPdf } from "@/lib/affiliate-statement";
import { toast } from "sonner";

export const Route = createFileRoute("/affiliate/payouts")({
  component: PayoutStatusPage,
  head: () => ({
    meta: [
      { title: "Affiliate payout status - Inquira" },
      { name: "description", content: "Track your Inquira creator commission balance, next payout window and latest payout batch." },
      { property: "og:title", content: "Inquira affiliate payout status" },
      { property: "og:description", content: "Commission balance, payout threshold and the status of your latest payout batch." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
});

type Payout = {
  id: string;
  batch_key: string | null;
  status: string;
  gross_cents: number;
  fee_cents: number;
  net_cents: number;
  currency: string | null;
  method: string | null;
  transaction_reference: string | null;
  period_start: string | null;
  period_end: string | null;
  paid_at: string | null;
  created_at: string;
  failure_reason?: string | null;
};

type Status = {
  ok: boolean;
  error?: string;
  minimum_payout_cents?: number;
  payout_frequency?: string;
  hold_period_days?: number;
  payout_method?: string | null;
  payout_email?: string | null;
  tax_status?: string | null;
  balances?: {
    pending_cents: number;
    approved_cents: number;
    available_cents: number;
    paid_cents: number;
    reversed_cents: number;
  };
  threshold_met?: boolean;
  shortfall_cents?: number;
  next_payout_window?: { period_start: string; period_end: string; run_on: string };
  latest_payout?: Payout | null;
  recent_payouts?: Array<{
    id: string;
    batch_key: string | null;
    status: string;
    amount_cents: number;
    fee_cents: number;
    net_cents: number;
    transaction_reference: string | null;
    paid_at: string | null;
    created_at: string;
  }>;
};

const money = (c: number) => `$${((c ?? 0) / 100).toFixed(2)}`;
const day = (d?: string | null) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

const BATCH_STATUS: Record<string, { text: string; cls: string }> = {
  pending: { text: "Queued", cls: "bg-amber-500/15 text-amber-600 dark:text-amber-400" },
  processing: { text: "Processing", cls: "bg-sky-500/15 text-sky-600 dark:text-sky-400" },
  paid: { text: "Paid", cls: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" },
  failed: { text: "Failed", cls: "bg-destructive/15 text-destructive" },
  cancelled: { text: "Cancelled", cls: "bg-muted text-muted-foreground" },
};

function Card({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <div className={`rounded-2xl border border-border bg-card p-5 ${className}`}>{children}</div>;
}

function Pill({ status }: { status: string }) {
  const meta = BATCH_STATUS[status] ?? { text: status, cls: "bg-muted text-muted-foreground" };
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${meta.cls}`}>{meta.text}</span>;
}

function PayoutStatusPage() {
  const { user, isLoading } = useAuth();
  const [data, setData] = useState<Status | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data: res, error } = await supabase.rpc("my_affiliate_payout_status" as never, {} as never);
    setLoading(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    setData((res as unknown as Status) ?? { ok: false });
  }, []);

  useEffect(() => {
    if (user) void load();
  }, [user, load]);

  async function statement(id: string, kind: "pdf" | "csv") {
    setBusy(id + kind);
    const s = await fetchPayoutStatement(id);
    if (!s.ok || !s.payout) {
      setBusy(null);
      return toast.error("Statement isn't available yet");
    }
    try {
      if (kind === "csv") downloadStatementCsv(s);
      else await downloadStatementPdf(s);
    } catch {
      toast.error("Could not generate the statement");
    } finally {
      setBusy(null);
    }
  }

  if (isLoading) return <div className="p-8 text-center">Loading…</div>;
  if (!user)
    return (
      <SignInRequired
        title="Sign in to see your payout status"
        description="Track your creator commission balance and payout batches."
      />
    );

  const b = data?.balances;
  const min = data?.minimum_payout_cents ?? 2500;
  const available = b?.available_cents ?? 0;
  const progress = Math.min(100, Math.round((available / Math.max(min, 1)) * 100));
  const latest = data?.latest_payout ?? null;

  return (
    <div className="mx-auto max-w-4xl p-4 sm:p-6">
      <Link to="/affiliate" className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground">
        <ArrowLeft className="h-4 w-4" /> Creator dashboard
      </Link>
      <h1 className="mb-2 text-3xl font-bold">Payout status</h1>
      <p className="mb-6 text-muted-foreground">
        Commissions clear after a {data?.hold_period_days ?? 30}-day hold, then pay out{" "}
        {data?.payout_frequency ?? "monthly"} once your available balance reaches {money(min)}.
      </p>

      {loading && !data ? (
        <Card>Loading your payout status…</Card>
      ) : !data?.ok ? (
        <Card>
          <p className="text-sm text-muted-foreground">
            You're not in the creator program yet.{" "}
            <Link to="/affiliates" className="text-primary underline">
              Apply here
            </Link>
            .
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          <Card>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Wallet className="h-4 w-4" /> Available to pay out
                </div>
                <div className="mt-1 text-4xl font-bold text-neon-pink">{money(available)}</div>
              </div>
              <div className="text-right text-sm">
                <div className="text-muted-foreground">Payout threshold</div>
                <div className="text-lg font-semibold">{money(min)}</div>
              </div>
            </div>
            <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-muted">
              <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${progress}%` }} />
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              {data.threshold_met
                ? "You're above the threshold — your balance is included in the next payout run."
                : `${money(data.shortfall_cents ?? 0)} to go. Balances below the threshold carry forward automatically.`}
            </p>
          </Card>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { label: "On hold", val: b?.pending_cents ?? 0 },
              { label: "Approved", val: b?.approved_cents ?? 0 },
              { label: "Paid to date", val: b?.paid_cents ?? 0 },
              { label: "Reversed", val: b?.reversed_cents ?? 0 },
            ].map((s) => (
              <Card key={s.label} className="p-4">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <DollarSign className="h-3 w-3" /> {s.label}
                </div>
                <div className="mt-1 text-xl font-bold">{money(s.val)}</div>
              </Card>
            ))}
          </div>

          <Card>
            <h2 className="inline-flex items-center gap-2 text-lg font-semibold">
              <CalendarClock className="h-4 w-4 text-neon-pink" /> Next payout window
            </h2>
            <div className="mt-3 grid gap-3 text-sm sm:grid-cols-3">
              <div className="rounded-xl border border-border p-3">
                <div className="text-xs text-muted-foreground">Earnings period</div>
                <div className="mt-1 font-medium">
                  {day(data.next_payout_window?.period_start)} – {day(data.next_payout_window?.period_end)}
                </div>
              </div>
              <div className="rounded-xl border border-border p-3">
                <div className="text-xs text-muted-foreground">Payout runs on</div>
                <div className="mt-1 font-medium">{day(data.next_payout_window?.run_on)}</div>
              </div>
              <div className="rounded-xl border border-border p-3">
                <div className="text-xs text-muted-foreground">Paid to</div>
                <div className="mt-1 truncate font-medium">
                  {data.payout_email ?? "—"}
                  <span className="ml-1 text-xs text-muted-foreground">({data.payout_method ?? "paypal"})</span>
                </div>
              </div>
            </div>
            {data.tax_status !== "approved" && (
              <p className="mt-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-xs">
                Your tax information is <strong>{data.tax_status ?? "required"}</strong>. Payouts are held until it's
                approved —{" "}
                <Link to="/affiliate" className="underline">
                  submit or update it here
                </Link>
                .
              </p>
            )}
          </Card>

          <Card>
            <h2 className="mb-3 text-lg font-semibold">Latest payout batch</h2>
            {!latest ? (
              <p className="text-sm text-muted-foreground">
                No payout batches yet. Your first batch is created once cleared commissions reach {money(min)}.
              </p>
            ) : (
              <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-3">
                  <Pill status={latest.status} />
                  <span className="text-sm font-medium">{latest.batch_key ?? latest.id.slice(0, 8)}</span>
                  <span className="text-xs text-muted-foreground">
                    created {day(latest.created_at)}
                    {latest.paid_at ? ` · paid ${day(latest.paid_at)}` : ""}
                  </span>
                </div>
                <div className="grid gap-2 text-sm sm:grid-cols-3">
                  <div className="rounded-xl border border-border p-3">
                    <div className="text-xs text-muted-foreground">Gross commission</div>
                    <div className="mt-1 font-semibold">{money(latest.gross_cents)}</div>
                  </div>
                  <div className="rounded-xl border border-border p-3">
                    <div className="text-xs text-muted-foreground">Processing fee</div>
                    <div className="mt-1 font-semibold">-{money(latest.fee_cents)}</div>
                  </div>
                  <div className="rounded-xl border border-border p-3">
                    <div className="text-xs text-muted-foreground">Net paid</div>
                    <div className="mt-1 font-semibold">{money(latest.net_cents)}</div>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground">
                  Transaction ID: <code>{latest.transaction_reference ?? "pending"}</code>
                  {latest.failure_reason ? ` · ${latest.failure_reason}` : ""}
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    disabled={busy === latest.id + "pdf"}
                    onClick={() => statement(latest.id, "pdf")}
                    className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-xs disabled:opacity-50"
                  >
                    <FileText className="h-3 w-3" /> Statement PDF
                  </button>
                  <button
                    disabled={busy === latest.id + "csv"}
                    onClick={() => statement(latest.id, "csv")}
                    className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-xs disabled:opacity-50"
                  >
                    <Download className="h-3 w-3" /> Statement CSV
                  </button>
                  <Link
                    to="/affiliate/statement/$id"
                    params={{ id: latest.id }}
                    className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-xs"
                  >
                    View full statement
                  </Link>
                </div>
              </div>
            )}
          </Card>

          {(data.recent_payouts?.length ?? 0) > 1 && (
            <Card>
              <h2 className="mb-3 text-lg font-semibold">Payout history</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs text-muted-foreground">
                    <tr>
                      <th className="py-2">Batch</th>
                      <th className="py-2">Status</th>
                      <th className="py-2">Gross</th>
                      <th className="py-2">Fee</th>
                      <th className="py-2">Net</th>
                      <th className="py-2">Statement</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {data.recent_payouts!.map((p) => (
                      <tr key={p.id}>
                        <td className="py-2">{p.batch_key ?? p.id.slice(0, 8)}</td>
                        <td className="py-2">
                          <Pill status={p.status} />
                        </td>
                        <td className="py-2">{money(p.amount_cents)}</td>
                        <td className="py-2">-{money(p.fee_cents)}</td>
                        <td className="py-2 font-medium">{money(p.net_cents)}</td>
                        <td className="py-2">
                          <button onClick={() => statement(p.id, "pdf")} className="text-xs text-primary underline">
                            PDF
                          </button>
                          <span className="px-1 text-muted-foreground">·</span>
                          <button onClick={() => statement(p.id, "csv")} className="text-xs text-primary underline">
                            CSV
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}